import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Avatar,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Business,
  People,
  TrendingUp,
  AttachMoney,
  MoreVert,
  Add,
  Visibility,
  Edit,
  Block,
} from "@mui/icons-material";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Bar,
  BarChart,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { superAdminAPI } from "../../services/api";
import { useSnackbar } from "notistack";
import { useAuth } from "../../contexts/AuthContext";

const StatCard = ({ title, value, subtitle, icon, color, trend, onClick }) => (
  <Card
    sx={{
      height: "100%",
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: onClick ? "translateY(-4px)" : "none",
        boxShadow: onClick ? 3 : 1,
      },
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" component="div" sx={{ fontWeight: 600 }}>
            {typeof value === "number" && !isNaN(value)
              ? value.toLocaleString()
              : value || 0}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              <TrendingUp
                sx={{ fontSize: 16, color: "success.main", mr: 0.5 }}
              />
              <Typography variant="caption" color="success.main">
                +{trend}% vs last month
              </Typography>
            </Box>
          )}
        </Box>
        <Avatar
          sx={{
            backgroundColor: color,
            width: 56,
            height: 56,
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [dashboardData, setDashboardData] = useState({
    stats: {},
    recentContractors: [],
    recentTenants: [],
    tenantContractorDistribution: [],
    loading: true,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const loadDashboardData = useCallback(async () => {
    try {
      // Use the correct superadmin dashboard API
      const response = await superAdminAPI.getDashboard();

      // The superadmin API returns data in response.data.data
      const data = response.data.data || {};

      setDashboardData({
        stats: {
          tenants: data.overview?.totalTenants || data.tenants?.total || 0,
          activeTenants:
            data.overview?.activeTenants || data.tenants?.active || 0,
          contractors:
            data.overview?.totalContractors ||
            data.usage?.totalContractors ||
            0,
          activeContractors:
            data.overview?.activeContractors ||
            data.usage?.activeContractors ||
            0,
          pendingContractors:
            data.overview?.pendingContractors ||
            data.usage?.pendingContractors ||
            0,
          users: data.overview?.totalUsers || data.users?.total || 0,
          activeUsers: data.overview?.activeUsers || 0,
        },
        recentContractors: data.recentContractors || [],
        recentTenants: data.recentTenants || [],
        tenantContractorDistribution: data.tenantContractorDistribution || [],
        loading: false,
      });
    } catch (error) {
      console.error("Superadmin Dashboard API Error:", error);
      enqueueSnackbar("Failed to load dashboard data", { variant: "error" });
      setDashboardData({
        stats: {},
        recentContractors: [],
        recentTenants: [],
        tenantContractorDistribution: [],
        loading: false,
      });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (user?.role !== "superadmin") {
      navigate("/dashboard");
      return;
    }
    loadDashboardData();
  }, [user, navigate, loadDashboardData]);

  const handleTenantMenuClick = (event, tenant) => {
    setAnchorEl(event.currentTarget);
    setSelectedTenant(tenant);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTenant(null);
  };

  const handleSuspendTenant = async (tenantId) => {
    if (window.confirm("Are you sure you want to suspend this tenant?")) {
      try {
        await superAdminAPI.suspendTenant(tenantId, "Suspended by admin");
        enqueueSnackbar("Tenant suspended successfully", {
          variant: "success",
        });
        loadDashboardData();
      } catch (error) {
        enqueueSnackbar("Failed to suspend tenant", { variant: "error" });
      }
    }
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "suspended":
        return "error";
      case "expired":
        return "warning";
      default:
        return "default";
    }
  };

  const subscriptionDistribution = [
    { name: "Starter", value: 45, color: "#90CAF9" },
    { name: "Professional", value: 35, color: "#1976D2" },
    { name: "Enterprise", value: 20, color: "#0D47A1" },
  ];

  if (dashboardData.loading) {
    return (
      <Box sx={{ width: "100%" }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Super Admin Dashboard 👑
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage tenants, subscriptions, and system-wide operations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/superadmin/tenants")}
          sx={{ borderRadius: 2 }}
        >
          Add Tenant
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Tenants"
            value={dashboardData.stats?.tenants || 0}
            subtitle={`${dashboardData.stats?.activeTenants || 0} active`}
            icon={<Business />}
            color="primary.main"
            trend={12}
            onClick={() => navigate("/superadmin/tenants")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Contractors"
            value={dashboardData.stats?.contractors || 0}
            subtitle={`${dashboardData.stats?.activeContractors || 0} active, ${
              dashboardData.stats?.pendingContractors || 0
            } pending`}
            icon={<People />}
            color="success.main"
            trend={8}
            onClick={() => navigate("/contractors")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Contractors"
            value={dashboardData.stats?.pendingContractors || 0}
            subtitle="Awaiting approval"
            icon={<TrendingUp />}
            color="warning.main"
            trend={0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={dashboardData.stats?.users || 0}
            subtitle={`${dashboardData.stats?.activeUsers || 0} active users`}
            icon={<AttachMoney />}
            color="secondary.main"
            trend={0}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Contractor Distribution Chart */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Contractor Distribution by Tenant
            </Typography>
            <ResponsiveContainer width="100%" height="80%">
              <BarChart data={dashboardData.tenantContractorDistribution || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="tenantName" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="total" fill="#1976d2" name="Total Contractors" />
                <Bar
                  dataKey="active"
                  fill="#4caf50"
                  name="Active Contractors"
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Subscription Distribution */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Subscription Plans
            </Typography>
            <ResponsiveContainer width="100%" height="60%">
              <PieChart>
                <Pie
                  data={subscriptionDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="value"
                >
                  {subscriptionDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2 }}>
              {subscriptionDistribution.map((item) => (
                <Box
                  key={item.name}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: item.color,
                      borderRadius: 1,
                      mr: 2,
                    }}
                  />
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {item.value}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Contractors */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Recent Contractors
              </Typography>
              <Button size="small" onClick={() => navigate("/contractors")}>
                View All
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell>Contractor ID</TableCell>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(dashboardData.recentContractors) ? (
                    dashboardData.recentContractors
                      .slice(0, 5)
                      .map((contractor) => (
                        <TableRow key={contractor._id}>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                                {contractor.companyName?.[0]?.toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {contractor.companyName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {
                                    contractor.contactDetails?.primaryContact
                                      ?.name
                                  }
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: "monospace" }}
                            >
                              {contractor.contractorId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {contractor.tenantId?.name || "N/A"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {contractor.tenantId?.domain || ""}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={contractor.status || "N/A"}
                              color={getStatusColor(contractor.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(
                                contractor.createdAt
                              ).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              onClick={(e) =>
                                handleTenantMenuClick(e, contractor)
                              }
                            >
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No contractors data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Tenant Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            // Handle view details
            handleMenuClose();
          }}
        >
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            // Handle edit tenant
            handleMenuClose();
          }}
        >
          <Edit sx={{ mr: 1 }} />
          Edit Tenant
        </MenuItem>
        <MenuItem onClick={() => handleSuspendTenant(selectedTenant?.id)}>
          <Block sx={{ mr: 1 }} />
          Suspend
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SuperAdminDashboard;
